import { _ } from "../polyglot/polyglot.js";
import { isNull } from "./object-util.js";
import moment from "./moment.js";

/**
 * In this function, moment will parse the date without any adjustment of
 * the local time zone and then normalize the time to 12:00:00.
 * Eg: "2005-12-31T00:00:00" -> "Sat Dec 31 2005 12:00:00 GMT+XXXX"
 * where "XXXX" is the local time zone the browser have.
 */
export function parse(dateString) {
	const date = newDate(dateString);
	return toNoon(date);
}

/**
 * Convert the input into a Date object.
 * The input can be anything accepted by moment.
 * It's usually a string, and eventually an array.
 *
 * @param {moment.MomentInput} momentInput - Object to parse
 * @return {Date}
 */
export function newDate(momentInput) {
	return moment(momentInput).toDate();
}

/**
 * Parse the input into a Date object, using the format "YYYYMMDD".
 *
 * @param {string} dateString - The string to parse
 * return {Date}
 */
export function parseFullDate(dateString) {
	return moment(dateString, "YYYYMMDD").toDate();
}

/**
 *
 * Normalize the time of a date to 12:00:00
 * This is useful to do when we want to avoid time zone converts
 * between displayed value and serialized value sent to the server.
 *
 * @param {Date} date - The date to normalize
 * @return {Date}
 */
export function toNoon(date) {
	if (isNull(date)) {
		return date;
	}

	const result = new Date(date);
	result.setHours(12);
	result.setMinutes(0);
	result.setSeconds(0);
	return result;
}

/**
 * Parse the input string (in UTC) into a Date object.
 *
 * @param {string} string - The string to parse
 * @return {Date}
 */
export function utcToDate(string) {
	if (string.indexOf("+") === -1 && string.indexOf("Z") === -1) {
		string += "+00:00";
	}

	return newDate(string);
}

/**
 * Given a date, return the last day of the month of which the given date
 * is part
 *
 * @param {Date} date - Date parameter from which to start computation
 * @return {Date}
 */
export function endOfMonth(date) {
	return toNoon(moment(date).endOf("month").toDate());
}

/**
 * Given a date, return the first day of the year of which the given date
 * is part
 *
 * @param {Date} date - Date parameter from which to start computation
 * @return {Date}
 */
export function startOfYear(date) {
	return toNoon(moment(date).startOf("year").toDate());
}

/**
 * Compute the number of days between two dates.
 *
 * @param {Date} date1 - The first date
 * @param {Date} date2 - The second date
 */
export function daysBetween(date1, date2) {
	const time = date1.getTime() - new Date(date2).getTime();
	return time / (1000 * 60 * 60 * 24);
}

/**
 * Take the beginning of `aDate`'s year, shift this by `index` `periodType` and return
 * the new date. For example, `withIndexAndType(new Date(2019, 9, 11), 3, 'Month')` returns
 * the date 2019/04/01.
 *
 * @param {int} index - The amount of `periodType` to shift from the beginning of `aDate` year.
 * @param {string} periodType - Either 'month' or 'quarter'.
 */
export function withIndexAndType({ date, index, periodType }) {
	return shift({
		date: moment(date).startOf("year").toDate(),
		amount: index,
		periodType,
	});
}

/**
 * Shift a date by a given amount of periodType.
 *
 * @param {object} object - Object parameters
 * @param {Date} object.date - The date to shift
 * @param {int} object.amount - The amount to shift
 * @param {string} object.periodType - The period type to shift
 * @return {Date}
 */
export function shift({ date, amount, periodType }) {
	if (periodType.toLowerCase() === "halfyear") {
		periodType = "month";
		amount *= 6;
	}

	return moment(date).add(amount, periodType).toDate();
}

/**
 * Check if the given date is the last day of the month.
 *
 * @param {Date} date - The date to check
 * @return {boolean}
 */
export function isLastDayOfMonth(date) {
	const endOfMonthDate = moment(date).endOf("month");
	const formattedEndOfMonthDate = moment(endOfMonthDate).format("MM/DD/YYYY");

	const formattedDate = moment(date).format("MM/DD/YYYY");

	return formattedDate === formattedEndOfMonthDate;
}

/**
 * Update the language of the moment library.
 *
 * @param {string} lang - The language to set, in the BCP 47 format
 */
export function setLanguage(lang) {
	lang = lang.toLowerCase();
	const momentFormatedLanguage = moment.locale(lang);

	moment.updateLocale(momentFormatedLanguage, {
		calendar: {
			lastDay: ["[", _("Yesterday"), "] (HH:mm)"].join(""),
			sameDay: "HH:mm",
			nextDay: ["[", _("Tomorrow"), "] (HH:mm)"].join(""),
			lastWeek: ["[", _("Last"), "] dddd (HH:mm)"].join(""),
			nextWeek: "dddd (HH:mm)",
			sameElse: "YYYY-MM-DD",
		},
	});
}

/**
 * Check if the two provided dates are the same day.
 *
 * @param {Date} oneDay - The first date to compare
 * @param {Date} anotherDay - The second date to compare
 * @return {boolean}
 */
export function isSameDay(oneDay, anotherDay) {
	return moment(oneDay).isSame(anotherDay, "day");
}

/**
 * Convert the provided duration to a number of months.
 *
 * @param {object} object - Object parameters
 * @param {Date} object.from - The start date of the duration
 * @param {Date} object.to - The end date of the duration
 * @return {number}
 */
export function durationAsMonths({ from, to }) {
	const momentEnd = moment(to);
	const momentStart = moment(from).add(1, "day");

	return momentEnd.diff(momentStart, "month") + 1;
}

/**
 * Return the day after the provided date.
 *
 * @param {Date} date - The date to shift
 * @return {Date}
 */
export function toNextDay(date) {
	return moment(date).add(1, "day").toDate();
}

/**
 * Return the month before the provided date.
 *
 * @param {Date} date - The date to shift
 * @return {Date}
 */
export function toPreviousMonth(date) {
	return moment(date).subtract(1, "months").toDate();
}

/**
 * Return the month after the provided date.
 *
 * @param {Date} date - The date to shift
 * @return {Date}
 */
export function toNextMonth(date) {
	return moment(date).add(1, "months").toDate();
}

/**
 * Sets the year to 1904 and adjust the date if it is the leap day.
 * This is useful for comparing period dates without the year in consideration.
 */
export function normalizeDate(date) {
	if (!moment.isMoment(date)) {
		date = moment(date);
	}
	date = date.year(1904);
	if (date.isSame(moment("1904-02-29"), "day")) {
		date = moment("1904-02-28");
	}
	return date;
}

// This ensure to be the last day of the month, no matter what
// By example if this.getEnd is 28th Feb.
/**
 * Adds a number of months to a date and returns the last day of the resulting
 * month.
 *
 * @param {Date} date - The date to which to add months
 * @param {number} numberOfMonths - The number of months to add
 * @return {Date}
 */
export function addMonths(date, numberOfMonths) {
	return moment(date)
		.add(numberOfMonths, "month")
		.subtract(1, "day")
		.toDate();
}

/**
 * Check if the two provided dates are consecutive days.
 *
 * @param {Date} oneDay - The first date to compare
 * @param {Date} anotherDay - The second date to compare
 * @return {boolean}
 */
export function areConsecutiveDays(oneDay, anotherDay) {
	return moment(anotherDay).subtract(1, "days").isSame(oneDay, "day");
}
